import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useSearchParams } from "react-router-dom";
import { fetchServices, fetchServiceById, fetchMenu, fetchSubServiceCards } from "../api";
import CategorySubServices from "./CategorySubServices";
import { DEV_TOOL_ICON_MAP } from "../data/devToolIcons";

const fallbackImage = "/images/ramadhan-kareem.png";

export default function ServicesPageHero() {
  const [searchParams] = useSearchParams();
  const serviceId = searchParams.get("service");
  const [content, setContent] = useState(null);
  const [typedTitle, setTypedTitle] = useState("");
  const [categoryChildren, setCategoryChildren] = useState([]);
  const [subServiceCards, setSubServiceCards] = useState([]);

  useEffect(() => {
    setContent(null);
    if (serviceId) {
      fetchServiceById(serviceId).then(setContent).catch(() => {});
    } else {
      // No service specified — feature the first active one, matching the Home Hero Carousel's order.
      fetchServices()
        .then((services) => {
          if (services.length > 0) setContent(services[0]);
        })
        .catch(() => {});
    }
  }, [serviceId]);

  // Each of the 6 services maps to a navbar "Services" category of the same title — show that
  // category's sub-service cards below the hero.
  useEffect(() => {
    if (!content) return;
    setCategoryChildren([]);
    Promise.all([fetchMenu(), fetchSubServiceCards()])
      .then(([items, cards]) => {
        setSubServiceCards(cards);
        const category = items.find((i) => i.title === content.title && i.level === 1);
        if (category) setCategoryChildren(items.filter((i) => i.parent === category.id));
      })
      .catch(() => {});
  }, [content]);

  // Typewriter effect: reveal the title one character at a time, matching the Home Hero Carousel.
  useEffect(() => {
    if (!content) return;
    const text = content.title || "";
    setTypedTitle("");
    let i = 0;
    const typeInterval = setInterval(() => {
      i += 1;
      setTypedTitle(text.slice(0, i));
      if (i >= text.length) clearInterval(typeInterval);
    }, 70);
    return () => clearInterval(typeInterval);
  }, [content]);

  useEffect(() => {
    if (content) document.title = `${content.title} - Mr. Aali Online Technologies`;
  }, [content]);

  if (!content) return null;

  const icons = (content.icons || "").split(",").filter(Boolean);
  const image = content.image || fallbackImage;

  return (
    <>
    <section id="services" className="relative min-h-[700px] scroll-mt-20 flex items-center text-white border-b border-navy-border bg-navy overflow-hidden">
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="floating-light absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-accent/20 blur-[120px]" style={{ animationDuration: "18s" }} />
        <div className="floating-light absolute -bottom-40 -right-20 w-[500px] h-[500px] rounded-full bg-accent-light/10 blur-[120px]" style={{ animationDuration: "22s", animationDelay: "2s" }} />
        <div className="floating-light absolute top-1/3 left-1/2 w-[350px] h-[350px] rounded-full bg-purple-500/10 blur-[100px]" style={{ animationDuration: "26s", animationDelay: "4s" }} />
        <div className="floating-light absolute bottom-10 left-10 w-[300px] h-[300px] rounded-full bg-accent-light/10 blur-[100px]" style={{ animationDuration: "20s", animationDelay: "1s" }} />
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-16 lg:px-[75px] py-16">
        <motion.div
          key={content.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="grid md:grid-cols-[40%_60%] gap-12 items-center"
        >
          {/* Image column */}
          <div className="flex items-center justify-center order-2 md:order-1">
            <img
              src={image}
              alt={content.title}
              className="w-full max-w-md rounded-xl shadow-2xl object-cover"
            />
          </div>

          {/* Content column */}
          <div className="text-center md:text-left order-1 md:order-2">
            <span className="text-accent-light uppercase tracking-widest text-sm font-semibold">
              Our Service
            </span>
            <h2 className="mt-3 text-3xl md:text-5xl font-extrabold tracking-tight">
              <span className="gradient-text-animated-warm">{typedTitle}</span>
              <span className="typing-cursor inline-block w-[3px] md:w-[4px] ml-1 bg-accent-light align-middle" style={{ height: "0.85em" }} />
            </h2>
            <p className="mt-6 text-lg text-gray-300">{content.description}</p>

            {icons.length > 0 && (
              <div className="mt-6 flex flex-wrap justify-center md:justify-start gap-3">
                {icons.map((key) => {
                  const entry = DEV_TOOL_ICON_MAP[key];
                  if (!entry) return null;
                  const { Icon, label } = entry;
                  return (
                    <span
                      key={key}
                      title={label}
                      className="flex items-center gap-2 px-3 py-2 rounded-md bg-white/5 border border-navy-border text-white/80 text-sm"
                    >
                      <Icon size={16} />
                      {label}
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </section>

    <CategorySubServices children={categoryChildren} subServiceCards={subServiceCards} />
    </>
  );
}
